import { useMemo } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridCapabilities } from "./tableOptions";
import type { DataGridInstance, UseDataGridOptions } from "./types";

type InstanceFlags<TData extends RowData> = Pick<
    DataGridInstance<TData>,
    | "density"
    | "canChangeDensity"
    | "defaultDensity"
    | "noun"
    | "gridLines"
    | "columnLayout"
    | "isTree"
    | "hasParentSummaries"
    | "hasCellNavigation"
    | "hasListEditing"
    | "listEditing"
    | "validateTotal"
    | "hasRangeSelection"
    | "hasRowOpen"
    | "isEditable"
    | "hasFillHandle"
    | "isServerPagination"
    | "hasRowReorder"
    | "hasColumnReorder"
    | "hasRowDragOut"
    | "motion"
>;

/**
 * The instance fields read straight from options and capabilities, as one
 * object that keeps its identity while none of them change.
 */
export const useInstanceFlags = <TData extends RowData>({
    options,
    capabilities,
}: {
    options: UseDataGridOptions<TData>;
    capabilities: DataGridCapabilities;
}): InstanceFlags<TData> => {
    const density = options.density ?? "default";
    const canChangeDensity = options.onDensityChange !== undefined;
    const { defaultDensity, listEditing } = options;
    const noun = options.noun ?? "rows";
    const hasCellNavigation =
        options.rangeSelection !== undefined ||
        options.editing !== undefined ||
        (options.cellNavigation ?? false);
    const validateTotal = options.editing?.validateTotal;
    const gridLines =
        options.gridLines ?? (hasCellNavigation ? "both" : "rows");
    const columnLayout = options.columnLayout ?? "start";
    const isEditable = options.editing !== undefined;
    const hasFillHandle =
        capabilities.hasRangeSelection && options.editing?.fillHandle === true;
    const hasRowReorder = options.rowReorder !== undefined;
    const hasColumnReorder = options.columnReorder ?? false;
    const hasRowDragOut =
        options.rowDragOut !== undefined && !capabilities.hasRangeSelection;
    const motionLevel = options.motion?.level ?? "full";
    const animateRowOrder = options.motion?.animateRowOrder ?? false;

    return useMemo(
        () => ({
            density,
            canChangeDensity,
            defaultDensity,
            noun,
            gridLines,
            columnLayout,
            isTree: capabilities.hasExpanding || capabilities.hasGrouping,
            hasParentSummaries: capabilities.hasParentSummaries,
            hasCellNavigation,
            hasListEditing: listEditing !== undefined,
            listEditing,
            validateTotal,
            hasRangeSelection: capabilities.hasRangeSelection,
            hasRowOpen: capabilities.hasRowOpen,
            isEditable,
            hasFillHandle,
            isServerPagination: capabilities.isServerPagination,
            hasRowReorder,
            hasColumnReorder,
            hasRowDragOut,
            motion: { level: motionLevel, animateRowOrder },
        }),
        [
            density,
            canChangeDensity,
            defaultDensity,
            noun,
            gridLines,
            columnLayout,
            capabilities,
            hasCellNavigation,
            listEditing,
            validateTotal,
            isEditable,
            hasFillHandle,
            hasRowReorder,
            hasColumnReorder,
            hasRowDragOut,
            motionLevel,
            animateRowOrder,
        ],
    );
};
